/**
 * PipelineDNA.tsx — /pipeline-dna  (own) | /pipeline-dna/:userId (drilldown)
 *
 * Per-AE aggregate helix. Resolves "me" to the authenticated user via
 * getSession(). Queries the user's real open accounts and per-CP answer
 * data from Supabase (tenant 1 only). Computes aggregate per-CP averages
 * across all open accounts and surfaces them in the OperationalHelix
 * (readOnly, accountId="aggregate") via initialAnswers.
 *
 * W62 fixes (2026-06-xx):
 *   A1. Header copy: "Pipeline DNA · {user.name} ({user.role})" when viewing
 *       own pipeline (/pipeline-dna or /pipeline-dna/:me); "Pipeline DNA ·
 *       {ae_name} (AE)" when manager drills into another user. Detected by
 *       comparing rawUserId to session.id.
 *   A2. Single source of truth for open deal count: accounts.length is the
 *       only counter used — SnapshotCard, top-risk copy, and account list
 *       header all read from the same value. "No open pipeline with scored
 *       data yet" replaced with a count-accurate fallback.
 *   A3. Bottom "Opportunity DNA" embedded block replaced: OperationalHelix
 *       (Three.js) only mounts when accounts.length > 0. The empty-state
 *       block has fixed height + contained styling. No overflow / clip.
 *
 * Earlier fixes (W6.5B — 2026-05-27):
 *   - Removed duplicate CpLegend render below OperationalHelix
 *   - Killed autoRotate
 *   - Added purpose header, subheader, 5-color legend above helix
 *   - Added per-CP account panel (tap an orb to see accounts for that CP)
 *   - Tenant 1 only throughout
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useRoute } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import OperationalHelix, {
  type AnswerStore,
  type CpAnswer,
} from "@/components/helix/OperationalHelix";
import {
  HELIX_CHECKPOINTS,
  type CheckpointId,
} from "@/lib/helix-checkpoints";
import { getSession, supabase } from "@/lib/session";
import { HEALTH_PALETTE } from "@shared/schema";
import { ALL_QUESTIONS } from "@shared/questions";
import { friendlyError } from "@/lib/friendly-error";

// ─── Palette (Mi6 canon UI palette) ──────────────────────────────────────────
const C = {
  black:    "#0B0B0F",
  red:      "#DC2626",
  white:    "#FAFAF7",
  charcoal: "#1F1F23",
  border:   "#E5E5E7",
  muted:    "#6B7280",
} as const;

// 5-color health legend (Mi6 canon — gray/red/orange/yellow/green)
const LEGEND_ITEMS = [
  { color: HEALTH_PALETTE.gray,   label: "Not scored" },
  { color: HEALTH_PALETTE.red,    label: "Bad" },
  { color: HEALTH_PALETTE.orange, label: "Concern" },
  { color: HEALTH_PALETTE.yellow, label: "Caution" },
  { color: HEALTH_PALETTE.green,  label: "Good" },
] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserRow {
  id: string;
  full_name: string | null;
  role: string | null;
  email: string | null;
}

interface AccountRow {
  id: number;
  name: string;
  status: string;
  industry: string | null;
  owner_user_id: string | null;
}

interface AnswerRowDb {
  account_id: number;
  question_key: string;
  selected_option: string | null;
  answer_notes?: string | null;
  updated_at?: string | null;
  evidence_type?: string | null;
  evidence_url?: string | null;
  evidence_file_name?: string | null;
  is_assumption?: boolean;
}

interface CompositeRow {
  account_id: string | null;
  composite_score: number | null;
}

// ─── Format helpers ───────────────────────────────────────────────────────────

/** Color for a composite 0-100 using canonical 5-step health palette */
function compositeToHealthColor(composite: number): string {
  if (composite >= 80) return HEALTH_PALETTE.green;
  if (composite >= 60) return HEALTH_PALETTE.yellow;
  if (composite >= 40) return HEALTH_PALETTE.orange;
  if (composite > 0)   return HEALTH_PALETTE.red;
  return HEALTH_PALETTE.gray;
}

function compositeToLabel(composite: number): string {
  if (composite >= 65) return "Strong";
  if (composite >= 50) return "Developing";
  if (composite >= 30) return "Early";
  if (composite > 0)   return "Weak";
  return "No data";
}

/** CP coverage pct → 5-color UI health palette (not forecast palette) */
function cpPctColor(pct: number): string {
  if (pct < 0)   return HEALTH_PALETTE.gray;   // not scored
  if (pct < 30)  return HEALTH_PALETTE.red;    // bad
  if (pct < 50)  return HEALTH_PALETTE.orange; // concern
  if (pct < 65)  return HEALTH_PALETTE.yellow; // caution
  return HEALTH_PALETTE.green;                  // good
}

/** Normalised 0-4 score → color dot for CP legend */
function cpScore4Color(score: number): string {
  if (score >= 3.5) return HEALTH_PALETTE.green;
  if (score >= 2.5) return HEALTH_PALETTE.yellow;
  if (score >= 1.5) return HEALTH_PALETTE.orange;
  if (score >= 0.5) return HEALTH_PALETTE.red;
  return HEALTH_PALETTE.gray;
}

// ─── Role display helper ──────────────────────────────────────────────────────

function fmtRole(role: string | null): string {
  if (!role) return "AE";
  const map: Record<string, string> = {
    ae:        "AE",
    bdr:       "BDR",
    manager:   "MANAGER",
    executive: "EXECUTIVE",
  };
  return map[role.toLowerCase()] ?? role.toUpperCase();
}

// ─── Resolve option points (mirrors OperationalHelix.resolveOptionPoints) ────

function resolveOptionPoints(
  q: (typeof ALL_QUESTIONS)[number],
  storedValue: string
): number | null {
  const maxPoints = Math.max(...q.options.map((o) => o.points));
  const asNumber = parseInt(storedValue, 10);
  if (!Number.isNaN(asNumber) && /^[0-9]+$/.test(storedValue)) {
    return Math.max(0, Math.min(asNumber, maxPoints));
  }
  const opt = q.options.find((o) => o.key === storedValue);
  if (!opt) return null;
  return Math.max(0, Math.min(opt.points, maxPoints));
}

// ─── Aggregate per-CP scores across multiple AnswerStores ────────────────────

interface CpAggregate {
  /** Per-CP average pct (0-100), -1 = no answers */
  cpScores: Record<CheckpointId, number>;
  /** Overall composite (0-100) */
  composite: number;
  /** CP with the lowest average pct */
  weakestCp: CheckpointId | null;
  /** Count of accounts where this CP has an answer score <= 25 pct */
  stuckPerCp: Record<CheckpointId, number>;
}

const CP_IDS = HELIX_CHECKPOINTS.map((cp) => cp.id);

function computeAggregate(
  accounts: AccountRow[],
  answersByAccount: Map<number, AnswerStore>
): CpAggregate {
  const cpScores = Object.fromEntries(
    CP_IDS.map((id) => [id, 0])
  ) as Record<CheckpointId, number>;
  const stuckPerCp = Object.fromEntries(
    CP_IDS.map((id) => [id, 0])
  ) as Record<CheckpointId, number>;

  for (const cp of HELIX_CHECKPOINTS) {
    const cpNum = cp.number;
    const cpQs = ALL_QUESTIONS.filter((q) => q.cp === cpNum);
    if (cpQs.length === 0) continue;

    let totalPct = 0;
    let countAccounts = 0;

    for (const account of accounts) {
      const store = answersByAccount.get(account.id) ?? {};
      let sumPoints = 0;
      let sumMax = 0;

      for (const q of cpQs) {
        const opt = store[q.key]?.selectedOption;
        if (opt === null || opt === undefined) continue;
        const pts = resolveOptionPoints(q, opt);
        if (pts === null) continue;
        const qMax = Math.max(...q.options.map((o) => o.points));
        sumPoints += pts;
        sumMax += qMax;
      }

      if (sumMax > 0) {
        const pct = Math.max(0, Math.min(100, (sumPoints / sumMax) * 100));
        totalPct += pct;
        countAccounts += 1;

        if (pct <= 25) {
          stuckPerCp[cp.id] = (stuckPerCp[cp.id] ?? 0) + 1;
        }
      }
    }

    cpScores[cp.id] = countAccounts > 0 ? totalPct / countAccounts : -1;
  }

  // Composite: average of answered CPs (only those with pct >= 0)
  let compositeSum = 0;
  let compositeCpCount = 0;
  for (const id of CP_IDS) {
    if (cpScores[id] >= 0) {
      compositeSum += cpScores[id];
      compositeCpCount += 1;
    }
  }
  const composite =
    compositeCpCount > 0 ? Math.round(compositeSum / compositeCpCount) : 0;

  // Weakest CP — lowest avg pct among those with any data
  let weakestCp: CheckpointId | null = null;
  let weakestScore = Infinity;
  for (const id of CP_IDS) {
    const s = cpScores[id];
    if (s >= 0 && s < weakestScore) {
      weakestScore = s;
      weakestCp = id;
    }
  }

  return { cpScores, composite, weakestCp, stuckPerCp };
}

// ─── Build initialAnswers for OperationalHelix aggregate sentinel ─────────────

function buildAggregateAnswerStore(
  accounts: AccountRow[],
  answersByAccount: Map<number, AnswerStore>
): AnswerStore {
  const store: AnswerStore = {};

  for (const q of ALL_QUESTIONS) {
    const scores: number[] = [];
    for (const account of accounts) {
      const acctStore = answersByAccount.get(account.id) ?? {};
      const opt = acctStore[q.key]?.selectedOption;
      if (opt === null || opt === undefined) continue;
      const pts = resolveOptionPoints(q, opt);
      if (pts === null) continue;
      const maxPts = Math.max(...q.options.map((o) => o.points));
      const scaled = maxPts > 0 ? (pts / maxPts) * 4 : 0;
      scores.push(scaled);
    }

    if (scores.length === 0) continue;

    const avg = scores.reduce((s, v) => s + v, 0) / scores.length;
    const rounded = String(Math.round(avg));

    const placeholder: CpAnswer = {
      selectedOption: rounded,
      notes: "",
      updatedAt: new Date().toISOString(),
      updatedBy: "aggregate",
    };
    store[q.key] = placeholder;
  }

  return store;
}

// ─── Snapshot card ────────────────────────────────────────────────────────────

function SnapshotCard({
  name,
  roleLabel,
  composite,
  openDealsCount,
  weakestCpName,
  topRisk,
}: {
  name: string;
  roleLabel: string;
  composite: number;
  openDealsCount: number;
  weakestCpName: string;
  topRisk: string;
}) {
  const color = compositeToHealthColor(composite);
  const label = compositeToLabel(composite);

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "DM Sans, Inter, sans-serif",
              fontSize: 20,
              fontWeight: 700,
              color: C.black,
              letterSpacing: -0.2,
            }}
          >
            {name}
          </div>
          <div
            style={{
              display: "inline-block",
              marginTop: 4,
              padding: "2px 8px",
              borderRadius: 999,
              background: C.black,
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            {roleLabel}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontFamily: "DM Sans, Inter, sans-serif",
              fontSize: 38,
              fontWeight: 700,
              lineHeight: 1,
              color: color,
            }}
          >
            {composite > 0 ? composite : "—"}
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              fontWeight: 600,
              marginTop: 4,
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            {label}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        {/* A2: single source of truth — openDealsCount passed from accounts.length */}
        <StatCell label="Open deals" value={String(openDealsCount)} />
        <StatCell label="Weakest CP" value={weakestCpName} />
        <StatCell label="Top risk" value={topRisk} small />
      </div>
    </div>
  );
}

function StatCell({
  label,
  value,
  small,
}: {
  label: string;
  value: string;
  small?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: C.muted,
          textTransform: "uppercase",
          letterSpacing: 0.6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: small ? 12 : 15,
          color: C.black,
          fontWeight: small ? 500 : 600,
          lineHeight: 1.3,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── CP legend (10 rows) ──────────────────────────────────────────────────────

function CpLegend({
  cpScores,
  selectedCp,
  onSelect,
}: {
  cpScores: Record<CheckpointId, number>;
  selectedCp: number | null;
  onSelect: (n: number | null) => void;
}) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 8,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: C.muted,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          padding: "8px 10px 4px",
        }}
      >
        Checkpoints
      </div>
      {HELIX_CHECKPOINTS.map((cp) => {
        const pct = cpScores[cp.id];
        const hasPct = pct >= 0;
        const score4 = hasPct ? (pct / 100) * 4 : 0;
        const active = selectedCp === cp.number;
        return (
          <button
            key={cp.id}
            type="button"
            onClick={() => onSelect(active ? null : cp.number)}
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "20px 1fr auto",
              gap: 10,
              alignItems: "center",
              padding: "8px 10px",
              border: "none",
              borderRadius: 8,
              background: active ? "rgba(220,38,38,0.08)" : "transparent",
              cursor: "pointer",
              textAlign: "left",
              transition: "background 120ms ease",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                background: hasPct ? cpScore4Color(score4) : C.border,
                justifySelf: "center",
              }}
            />
            <span
              title={`CP${cp.number} — ${cp.name}`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                color: C.black,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 180,
              }}
            >
              CP{cp.number} — {cp.name}
            </span>
            <span
              style={{
                fontFamily: "DM Sans, Inter, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                color: hasPct ? cpScore4Color(score4) : C.muted,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {hasPct ? `${Math.round(pct)}%` : "—"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Purpose header + color legend ───────────────────────────────────────────

function HelixPurposeHeader() {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "16px 20px",
      }}
    >
      <h2
        style={{
          fontFamily: "DM Sans, Inter, sans-serif",
          fontSize: 16,
          fontWeight: 700,
          color: C.black,
          margin: 0,
          letterSpacing: -0.1,
        }}
      >
        Qualified pipeline only (CP1+)
      </h2>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          color: C.muted,
          margin: "6px 0 12px",
          lineHeight: 1.5,
        }}
      >
        Each orb represents a checkpoint (CP1–CP10). Color reflects average
        coverage state across all open accounts. Tap an orb to see which
        accounts are covered or uncovered for that checkpoint.
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 16px",
          paddingTop: 10,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        {LEGEND_ITEMS.map((item) => (
          <div
            key={item.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                background: item.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                color: C.black,
                fontWeight: 500,
              }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Per-CP account coverage ──────────────────────────────────────────────────

interface AccountCpInfo {
  accountId: number;
  accountName: string;
  status: string;
  cpPct: number;
  composite: number;
}

function computeAccountCpInfos(
  cpNumber: number,
  accounts: AccountRow[],
  answersByAccount: Map<number, AnswerStore>,
  compositesByAccount: Map<number, number>
): AccountCpInfo[] {
  const cpQs = ALL_QUESTIONS.filter((q) => q.cp === cpNumber);
  return accounts.map((account) => {
    const store = answersByAccount.get(account.id) ?? {};
    let sumPoints = 0;
    let sumMax = 0;
    for (const q of cpQs) {
      const opt = store[q.key]?.selectedOption;
      if (opt === null || opt === undefined) continue;
      const pts = resolveOptionPoints(q, opt);
      if (pts === null) continue;
      const qMax = Math.max(...q.options.map((o) => o.points));
      sumPoints += pts;
      sumMax += qMax;
    }
    const cpPct = sumMax > 0
      ? Math.max(0, Math.min(100, Math.round((sumPoints / sumMax) * 100)))
      : -1;
    return {
      accountId: account.id,
      accountName: account.name,
      status: account.status,
      cpPct,
      composite: compositesByAccount.get(account.id) ?? 0,
    };
  }).sort((a, b) => {
    if (a.cpPct < 0 && b.cpPct >= 0) return 1;
    if (a.cpPct >= 0 && b.cpPct < 0) return -1;
    return b.cpPct - a.cpPct;
  });
}

// ─── Per-CP account side panel ────────────────────────────────────────────────

function CpAccountPanel({
  cpNumber,
  accounts,
  answersByAccount,
  compositesByAccount,
  onClose,
}: {
  cpNumber: number | null;
  accounts: AccountRow[];
  answersByAccount: Map<number, AnswerStore>;
  compositesByAccount: Map<number, number>;
  onClose: () => void;
}) {
  if (cpNumber === null) return null;

  const cpDef = HELIX_CHECKPOINTS.find((c) => c.number === cpNumber);
  const accountInfos = computeAccountCpInfos(
    cpNumber,
    accounts,
    answersByAccount,
    compositesByAccount
  );

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 16px",
          borderBottom: `1px solid ${C.border}`,
          background: "#1F1F23",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#525258",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 2,
            }}
          >
            Checkpoint
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "#EFEFEE",
              letterSpacing: -0.1,
            }}
          >
            CP{cpNumber} — {cpDef?.name ?? ""}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          style={{
            background: "transparent",
            border: "none",
            color: "#6B6B70",
            fontSize: 18,
            cursor: "pointer",
            lineHeight: 1,
            padding: "4px 8px",
            fontFamily: "Inter, sans-serif",
          }}
        >
          Close
        </button>
      </div>

      <div style={{ maxHeight: 420, overflowY: "auto" }}>
        {accountInfos.length === 0 ? (
          <div
            style={{
              padding: 20,
              fontSize: 13,
              color: C.muted,
              fontFamily: "Inter, sans-serif",
            }}
          >
            No accounts in pipeline.
          </div>
        ) : (
          accountInfos.map((info) => (
            <div
              key={info.accountId}
              style={{
                padding: "12px 16px",
                borderBottom: `1px solid ${C.border}`,
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  title={info.accountName}
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.black,
                    marginBottom: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 180,
                  }}
                >
                  {info.accountName}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: C.muted,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {info.status}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: cpPctColor(info.cpPct),
                      fontWeight: 700,
                      fontFamily: "DM Sans, Inter, sans-serif",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {info.cpPct >= 0 ? `CP coverage ${info.cpPct}%` : "Not scored"}
                  </span>
                </div>
                {info.composite > 0 && (
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 11,
                      color: C.muted,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Composite score:{" "}
                    <span
                      style={{
                        fontWeight: 700,
                        color: compositeToHealthColor(info.composite),
                      }}
                    >
                      {info.composite}
                    </span>
                  </div>
                )}
              </div>
              <Link href={`/account/${info.accountId}`}>
                <a
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.red,
                    textDecoration: "none",
                    fontFamily: "Inter, sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  Open account
                </a>
              </Link>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Account row (open pipeline) ─────────────────────────────────────────────

function AccountListRow({
  account,
  composite,
}: {
  account: AccountRow;
  composite: number;
}) {
  return (
    <Link href={`/account/${account.id}`}>
      <a
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 12,
          alignItems: "center",
          padding: "12px 14px",
          background: "#fff",
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          textDecoration: "none",
          color: "inherit",
          marginBottom: 8,
          transition: "border-color 120ms ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = C.black;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = C.border;
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: C.black,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {account.name}
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              marginTop: 2,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{account.status}</span>
            {account.industry && (
              <>
                <span aria-hidden>·</span>
                <span>{account.industry}</span>
              </>
            )}
          </div>
        </div>
        {composite > 0 && (
          <div
            style={{
              fontFamily: "DM Sans, Inter, sans-serif",
              fontSize: 13,
              fontWeight: 700,
              color: compositeToHealthColor(composite),
              background: "rgba(0,0,0,0.04)",
              padding: "4px 10px",
              borderRadius: 999,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {composite}
          </div>
        )}
      </a>
    </Link>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PipelineDNA() {
  // A1: Detect own vs drilldown
  const [, params] = useRoute<{ userId: string }>("/pipeline-dna/:userId");
  const rawUserId = params?.userId ?? "me";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [userRow, setUserRow] = useState<UserRow | null>(null);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [answersByAccount, setAnswersByAccount] = useState<Map<number, AnswerStore>>(new Map());
  const [compositesByAccount, setCompositesByAccount] = useState<Map<number, number>>(new Map());

  const [selectedCp, setSelectedCp] = useState<number | null>(null);

  const session = getSession();

  // A1: "own" if no param or param matches session user
  const isOwnView = rawUserId === "me" || rawUserId === session?.id;

  const resolvedUserId: string | null = useMemo(() => {
    if (rawUserId === "me") {
      return session?.id ?? null;
    }
    return rawUserId || null;
  }, [rawUserId, session?.id]);

  // ── Fetch data from Supabase ──────────────────────────────────────────────
  useEffect(() => {
    if (!resolvedUserId) {
      setLoading(false);
      setError("No authenticated session. Please sign in.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const sb = supabase();
        const activeTenantId = session?.tenantId ?? 1;

        // 1. User row
        const { data: uData, error: uErr } = await sb
          .from("users")
          .select("id, full_name, role, email")
          .eq("tenant_id", activeTenantId)
          .eq("id", resolvedUserId)
          .limit(1)
          .maybeSingle();

        if (uErr) throw new Error(`User lookup failed: ${uErr.message}`);
        if (!uData) {
          if (!cancelled) {
            setError(
              "No user found for your session in this tenant. " +
              "Contact your administrator if this is unexpected."
            );
            setLoading(false);
          }
          return;
        }

        // 2. User's open accounts — qualified pipeline only (exclude Suspect/Prospect)
        //    Also exclude [SIM-% simulation accounts (Tenant 1 canon guard)
        const OPEN_STAGES = [
          "Qualified Opp",
          "Full Opportunity",
          "Won",
        ];
        const { data: acctData, error: acctErr } = await sb
          .from("accounts")
          .select("id, name, status, industry, owner_user_id")
          .eq("tenant_id", activeTenantId)
          .eq("owner_user_id", resolvedUserId)
          .in("status", OPEN_STAGES)
          .not("name", "like", "[SIM-%");

        if (acctErr) throw new Error(`Accounts lookup failed: ${acctErr.message}`);
        const acctRows: AccountRow[] = (acctData ?? []).map((r: any) => ({
          id: r.id,
          name: r.name ?? "(unnamed)",
          status: r.status ?? "Qualified Opp",
          industry: r.industry ?? null,
          owner_user_id: r.owner_user_id ?? null,
        }));

        if (acctRows.length === 0) {
          if (!cancelled) {
            setUserRow(uData as UserRow);
            setAccounts([]);
            setAnswersByAccount(new Map());
            setCompositesByAccount(new Map());
            setLoading(false);
          }
          return;
        }

        const acctIds = acctRows.map((a) => a.id);

        // 3. account_answers for all open accounts
        const { data: ansData, error: ansErr } = await sb
          .from("account_answers")
          .select(
            "account_id, question_key, selected_option, answer_notes, updated_at, evidence_type, evidence_url, evidence_file_name, is_assumption"
          )
          .in("account_id", acctIds);

        if (ansErr) throw new Error(`Answers lookup failed: ${ansErr.message}`);

        const byAcct = new Map<number, AnswerStore>();
        for (const row of (ansData ?? []) as AnswerRowDb[]) {
          if (!row.selected_option) continue;
          let store = byAcct.get(row.account_id);
          if (!store) {
            store = {};
            byAcct.set(row.account_id, store);
          }
          store[row.question_key] = {
            selectedOption: row.selected_option,
            notes: row.answer_notes ?? "",
            updatedAt: row.updated_at ?? new Date().toISOString(),
            updatedBy: "db",
            evidenceType: (row.evidence_type as any) ?? null,
            evidenceUrl: row.evidence_url ?? null,
            evidenceFileName: row.evidence_file_name ?? null,
            isAssumption: row.is_assumption ?? false,
          };
        }

        // 4. account_plans for composite scores
        const { data: planData } = await sb
          .from("account_plans")
          .select("account_id, composite_score")
          .in("account_id", acctIds.map(String));

        const composites = new Map<number, number>();
        for (const row of (planData ?? []) as CompositeRow[]) {
          if (row.composite_score != null && row.account_id != null) {
            const acctId = Number(row.account_id);
            if (!isNaN(acctId)) {
              composites.set(acctId, Number(row.composite_score));
            }
          }
        }

        if (!cancelled) {
          setUserRow(uData as UserRow);
          setAccounts(acctRows);
          setAnswersByAccount(byAcct);
          setCompositesByAccount(composites);
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(friendlyError(e));
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [resolvedUserId]);

  // ── Aggregate computation ─────────────────────────────────────────────────
  const aggregate = useMemo(
    () => computeAggregate(accounts, answersByAccount),
    [accounts, answersByAccount]
  );

  const aggregateAnswerStore = useMemo(
    () => buildAggregateAnswerStore(accounts, answersByAccount),
    [accounts, answersByAccount]
  );

  // ── A2: single source of truth for open deal count ────────────────────────
  const openDealsCount = accounts.length;

  // ── Snapshot fields ───────────────────────────────────────────────────────
  const weakestCpDef = aggregate.weakestCp
    ? HELIX_CHECKPOINTS.find((c) => c.id === aggregate.weakestCp)
    : null;
  const weakestCpName = weakestCpDef
    ? `CP${weakestCpDef.number} — ${weakestCpDef.name}`
    : "—";

  // A2: top risk copy unified — no separate "no scored data" string
  const topRisk = useMemo(() => {
    if (openDealsCount === 0) return "No open deals in pipeline.";
    if (!weakestCpDef) return `${openDealsCount} open deal${openDealsCount === 1 ? "" : "s"} — no CP scores recorded yet.`;
    const stuck = aggregate.stuckPerCp[weakestCpDef.id] ?? 0;
    if (stuck === 0)
      return `${weakestCpDef.name} is the weakest CP, no deal critically stuck on it.`;
    return `${stuck} deal${stuck === 1 ? "" : "s"} stuck on ${weakestCpDef.name}`;
  }, [openDealsCount, weakestCpDef, aggregate.stuckPerCp]);

  // A1: Header subtitle — own view vs drilldown
  const displayName = userRow?.full_name ?? userRow?.email ?? "Unknown";
  const displayRole = userRow?.role ?? "AE";
  const roleLabel = fmtRole(displayRole);

  // Page meta copy: "Pipeline DNA · {name} ({role})" for own,
  // "Pipeline DNA · {ae_name} (AE)" for drilldown
  const headerSubtitle = isOwnView
    ? `${displayName} (${roleLabel})`
    : `${displayName} (${roleLabel})`;

  // ── Sorted accounts (highest composite first) ─────────────────────────────
  const sortedAccounts = useMemo(
    () =>
      [...accounts].sort((a, b) => {
        const ca = compositesByAccount.get(a.id) ?? 0;
        const cb = compositesByAccount.get(b.id) ?? 0;
        return cb - ca;
      }),
    [accounts, compositesByAccount]
  );

  // ─── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Pipeline DNA" }]} pageTitle="Pipeline DNA" pageMeta="Pipeline DNA">
        <div
          style={{
            padding: 32,
            fontFamily: "Inter, sans-serif",
            color: C.muted,
            fontSize: 14,
          }}
        >
          Loading pipeline data...
        </div>
      </OperatorShell>
    );
  }

  // ─── Error state ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Pipeline DNA" }]} pageTitle="Pipeline DNA" pageMeta="Pipeline DNA">
        <div style={{ padding: 32, fontFamily: "Inter, sans-serif" }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: C.red }}>
            Unable to load pipeline data
          </h1>
          <p style={{ fontSize: 14, color: C.muted, marginTop: 8 }}>{error}</p>
          <Link href="/team-dna">
            <a
              style={{
                display: "inline-block",
                marginTop: 16,
                color: C.red,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Back to Team DNA
            </a>
          </Link>
        </div>
      </OperatorShell>
    );
  }

  // ─── User not found ───────────────────────────────────────────────────────
  if (!userRow) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Pipeline DNA" }]} pageTitle="Pipeline DNA" pageMeta="Pipeline DNA">
        <div style={{ padding: 32, fontFamily: "Inter, sans-serif" }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.black }}>
            User not found
          </h1>
          <p style={{ fontSize: 14, color: C.muted, marginTop: 8 }}>
            No user with that ID was found in this tenant.
          </p>
          <Link href="/team-dna">
            <a
              style={{
                display: "inline-block",
                marginTop: 16,
                color: C.red,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Back to Team DNA
            </a>
          </Link>
        </div>
      </OperatorShell>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    // A1: pageMeta now reads "Pipeline DNA · {name} ({role})" not "per-AE aggregate"
    <OperatorShell
      breadcrumbs={[{ label: "Pipeline DNA" }]}
      pageTitle="Pipeline DNA"
      pageMeta={`Pipeline DNA · ${headerSubtitle}`}
    >
      <div
        style={{
          padding: "20px clamp(16px, 4vw, 32px) 60px",
          background: C.white,
          minHeight: "100vh",
        }}
      >
        {/* A1: Header — title + subtitle on same line */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "DM Sans, Inter, sans-serif",
                fontSize: "clamp(22px, 3vw, 28px)",
                fontWeight: 700,
                color: C.black,
                letterSpacing: -0.3,
                margin: 0,
              }}
            >
              Pipeline DNA
            </h1>
            {/* A1: subtitle shows name + role — no more "per-AE aggregate" */}
            <div
              style={{
                marginTop: 4,
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                color: C.muted,
              }}
            >
              {headerSubtitle}
            </div>
          </div>
          <Link href="/team-dna">
            <a
              style={{
                color: C.red,
                fontWeight: 600,
                fontSize: 13,
                textDecoration: "none",
                fontFamily: "Inter, sans-serif",
              }}
            >
              ← Team DNA
            </a>
          </Link>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 24,
          }}
        >
          <div
            style={{
              display: "grid",
              gap: 24,
              gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
            }}
            className="pipeline-dna-grid"
          >
            {/* ── Left column ─────────────────────────────────────────── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* A2: openDealsCount is the single source of truth */}
              <SnapshotCard
                name={displayName}
                roleLabel={roleLabel}
                composite={aggregate.composite}
                openDealsCount={openDealsCount}
                weakestCpName={weakestCpName}
                topRisk={topRisk}
              />

              <HelixPurposeHeader />

              {/* A3: OperationalHelix only mounts when there are accounts.
                   Empty state is a fixed-height contained block (no Three.js
                   mount = no overflow / clip). */}
              {openDealsCount > 0 ? (
                <OperationalHelix
                  accountId="aggregate"
                  accountName={`${displayName} — Pipeline Aggregate`}
                  industry={undefined}
                  initialAnswers={aggregateAnswerStore}
                  readOnly={true}
                  focusCp={selectedCp}
                />
              ) : (
                // A3: fixed-height contained block — no overflow, no clip
                <div
                  style={{
                    background: "#0A0A0C",
                    borderRadius: 12,
                    height: 320,
                    maxHeight: 320,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      padding: "0 24px",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "DM Sans, Inter, sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#6B6B70",
                        letterSpacing: 0.2,
                      }}
                    >
                      No open accounts with scored data yet
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12,
                        color: "#525258",
                      }}
                    >
                      {/* A2: count is consistent — same accounts.length */}
                      {openDealsCount === 0
                        ? "Add a Qualified Opp or Full Opportunity account to build the aggregate helix."
                        : `${openDealsCount} open deal${openDealsCount === 1 ? "" : "s"} found — answer M-PACT questions to populate the helix.`}
                    </div>
                  </div>
                </div>
              )}

              {openDealsCount > 0 && selectedCp !== null && (
                <CpAccountPanel
                  cpNumber={selectedCp}
                  accounts={accounts}
                  answersByAccount={answersByAccount}
                  compositesByAccount={compositesByAccount}
                  onClose={() => setSelectedCp(null)}
                />
              )}
            </div>

            {/* ── Right column ────────────────────────────────────────── */}
            <div>
              <CpLegend
                cpScores={aggregate.cpScores}
                selectedCp={selectedCp}
                onSelect={setSelectedCp}
              />

              <div
                style={{
                  fontFamily: "DM Sans, Inter, sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  color: C.black,
                  marginBottom: 8,
                  marginTop: 20,
                  letterSpacing: -0.1,
                }}
              >
                {/* A2: count uses same openDealsCount */}
                Open accounts ({openDealsCount})
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: C.muted,
                  marginBottom: 16,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Click any account to open its Opportunity DNA.
              </div>
              {sortedAccounts.length === 0 ? (
                <div
                  style={{
                    background: "#fff",
                    border: `1px dashed ${C.border}`,
                    borderRadius: 10,
                    padding: 16,
                    fontSize: 13,
                    color: C.muted,
                  }}
                >
                  No open accounts in pipeline.
                </div>
              ) : (
                sortedAccounts.map((a) => (
                  <AccountListRow
                    key={a.id}
                    account={a}
                    composite={compositesByAccount.get(a.id) ?? 0}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <style>
          {`
            @media (max-width: 900px) {
              .pipeline-dna-grid {
                grid-template-columns: minmax(0, 1fr) !important;
              }
            }
          `}
        </style>
      </div>
    </OperatorShell>
  );
}
